const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const BAD_CONDITION_KEYWORDS = [
  'RAIN', 'HEAVY_RAIN', 'THUNDERSTORM', 'SNOW', 'SLEET',
  'DRIZZLE', 'HAIL', 'FREEZING', 'BLIZZARD', 'ICE', 'STORM', 'TORNADO',
];

export async function geocodeAddress(address) {
  const res = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${KEY}`
  );
  const data = await res.json();
  const loc = data.results?.[0]?.geometry?.location;
  if (!loc) throw new Error(`Could not geocode: ${address}`);
  return { lat: loc.lat, lng: loc.lng };
}

export async function getDailyForecast(lat, lng) {
  const res = await fetch(
    `https://weather.googleapis.com/v1/forecast/days:lookup?key=${KEY}&location.latitude=${lat}&location.longitude=${lng}&days=10`
  );
  if (!res.ok) throw new Error(`Weather API error: ${res.status}`);
  const data = await res.json();
  return data.forecastDays || [];
}

function isBadForDetailing(dayForecast) {
  const f = dayForecast?.daytimeForecast || {};
  const rainChance = f.precipitation?.probability?.percent ?? 0;
  const windVal = f.wind?.speed?.value ?? 0;
  const windUnit = (f.wind?.speed?.unit || '').toUpperCase();
  const windMph = windUnit.includes('KPH') || windUnit.includes('KM') ? windVal * 0.621371 : windVal;
  const condType = (f.weatherCondition?.type || '').toUpperCase();
  const tempHigh = f.temperature?.high?.degrees ?? 60;
  const tempUnit = (f.temperature?.high?.unit || 'FAHRENHEIT').toUpperCase();
  const tempF = tempUnit === 'CELSIUS' ? (tempHigh * 9 / 5) + 32 : tempHigh;

  return (
    rainChance > 40 ||
    BAD_CONDITION_KEYWORDS.some(c => condType.includes(c)) ||
    windMph > 25 ||
    tempF < 35
  );
}

function buildAlertReason(dayForecast) {
  const f = dayForecast?.daytimeForecast || {};
  const reasons = [];
  const rainChance = f.precipitation?.probability?.percent ?? 0;
  const windVal = f.wind?.speed?.value ?? 0;
  const windUnit = (f.wind?.speed?.unit || '').toUpperCase();
  const windMph = windUnit.includes('KPH') || windUnit.includes('KM') ? windVal * 0.621371 : windVal;
  const condType = (f.weatherCondition?.type || '').toUpperCase();
  const condText = f.weatherCondition?.description?.text || '';
  const tempHigh = f.temperature?.high?.degrees ?? 60;
  const tempUnit = (f.temperature?.high?.unit || 'FAHRENHEIT').toUpperCase();
  const tempF = tempUnit === 'CELSIUS' ? (tempHigh * 9 / 5) + 32 : tempHigh;

  if (rainChance > 40) reasons.push(`${rainChance}% rain`);
  if (BAD_CONDITION_KEYWORDS.some(c => condType.includes(c)) && condText) reasons.push(condText);
  if (windMph > 25) reasons.push(`${Math.round(windMph)} mph winds`);
  if (tempF < 35) reasons.push(`High of ${Math.round(tempF)}°F`);
  return reasons.join(' · ') || 'Poor conditions';
}

export function conditionToIcon(condType) {
  const t = (condType || '').toUpperCase();
  if (t.includes('THUNDER') || t.includes('STORM')) return '⛈️';
  if (t.includes('SNOW') || t.includes('BLIZZARD')) return '❄️';
  if (t.includes('SLEET') || t.includes('FREEZING')) return '🌨️';
  if (t.includes('RAIN') || t.includes('DRIZZLE')) return '🌧️';
  if (t.includes('OVERCAST')) return '☁️';
  if (t.includes('CLOUD')) return '⛅';
  if (t.includes('CLEAR') || t.includes('SUNNY')) return '☀️';
  return '🌤️';
}

export function getWeatherIcon(dayForecast) {
  return conditionToIcon(dayForecast?.daytimeForecast?.weatherCondition?.type);
}

export async function getHourlyForecast(lat, lng) {
  const res = await fetch(
    `https://weather.googleapis.com/v1/forecast/hours:lookup?key=${KEY}` +
    `&location.latitude=${lat}&location.longitude=${lng}&hours=240`
  );
  if (!res.ok) throw new Error(`Weather hourly API error: ${res.status}`);
  const data = await res.json();
  return data.forecastHours || [];
}

export function getForecastSummary(dayForecast) {
  const f = dayForecast?.daytimeForecast || {};
  const rainChance = f.precipitation?.probability?.percent ?? 0;
  const condText = f.weatherCondition?.description?.text || '';
  const tempHigh = f.temperature?.high?.degrees ?? null;
  const tempUnit = (f.temperature?.high?.unit || 'FAHRENHEIT').toUpperCase();
  const tempF = tempHigh !== null
    ? (tempUnit === 'CELSIUS' ? Math.round((tempHigh * 9 / 5) + 32) : Math.round(tempHigh))
    : null;

  return { condText, rainChance, tempF };
}

export async function checkBookingsWeather(bookings) {
  const now = new Date();
  const tenDaysOut = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000);

  const upcoming = bookings.filter(b => {
    if (!b.scheduled_at || !b.address) return false;
    if (b.status === 'cancelled' || b.status === 'completed') return false;
    const d = new Date(b.scheduled_at);
    return d >= now && d <= tenDaysOut;
  });

  const alerts = [];
  for (const booking of upcoming) {
    try {
      const { lat, lng } = await geocodeAddress(booking.address);
      const days = await getDailyForecast(lat, lng);
      const bookingDate = new Date(booking.scheduled_at);
      bookingDate.setHours(0, 0, 0, 0);

      const dayForecast = days.find(d => {
        const start = new Date(d.interval?.startTime);
        start.setHours(0, 0, 0, 0);
        return start.getTime() === bookingDate.getTime();
      });

      if (dayForecast && isBadForDetailing(dayForecast)) {
        alerts.push({
          booking,
          forecast: dayForecast,
          reason: buildAlertReason(dayForecast),
          icon: getWeatherIcon(dayForecast),
          summary: getForecastSummary(dayForecast),
        });
      }
    } catch (err) {
      console.warn('Weather check skipped for booking', booking.id, ':', err.message);
    }
  }
  return alerts;
}
